import * as Monaco from 'monaco-editor';
import { Project } from '../types';

interface TypeDefinitionCache {
    [key: string]: {
        content: string;
        timestamp: number;
    };
}

export class JSTypeDefinitionService {
    private cache: TypeDefinitionCache = {};
    private cacheExpiry = 24 * 60 * 60 * 1000;
    private monaco: typeof Monaco;
    private project: Project;
    private inFlightRequests: Map<string, Promise<string>> = new Map();
    
    // Packages that don't need type definitions (build tools, CSS processors, etc.)
    private readonly skipPackages = new Set([
        'tailwindcss',
        'postcss',
        'autoprefixer',
        'vite',
        'eslint',
        '@vitejs/plugin-react',
        '@vitejs/plugin-react-swc',
        'typescript', // Already handled by Monaco
    ]);

    constructor(monaco: typeof Monaco, project: Project) {
        this.monaco = monaco;
        this.project = project;
    }

    public getProject(): Project {
        return this.project;
    }

    private getDfinityPaths(packageName: string, version: string): string[] {
        // Remove @ from version if present
        const cleanVersion = version.startsWith('@') ? version.slice(1) : version;
        
        // Use CORS proxy for unpkg.com to avoid CORS issues on IC network
        const corsProxy = 'https://api.allorigins.win/raw?url=';
        const encodeUrl = (url: string) => corsProxy + encodeURIComponent(url);

        if (packageName === '@dfinity/agent') {
            return [
                encodeUrl(`https://unpkg.com/@dfinity/agent@${cleanVersion}/lib/cjs/index.d.ts`),
                encodeUrl(`https://unpkg.com/@dfinity/agent@${cleanVersion}/lib/esm/index.d.ts`),
                encodeUrl(`https://unpkg.com/@dfinity/agent/lib/cjs/index.d.ts`)
            ];
        }

        if (packageName === '@dfinity/principal') {
            return [
                encodeUrl(`https://unpkg.com/@dfinity/principal@${cleanVersion}/lib/cjs/index.d.ts`),
                encodeUrl(`https://unpkg.com/@dfinity/principal@${cleanVersion}/lib/esm/index.d.ts`),
                encodeUrl(`https://unpkg.com/@dfinity/principal/lib/cjs/index.d.ts`)
            ];
        }

        return [];
    }

    private getRegularPaths(packageName: string, version?: string): string[] {
        const paths = [];
        const typesPackage = packageName.startsWith('@types/') ?
            packageName :
            `@types/${packageName.replace(/^@.*\//, '')}`;

        // Use CORS proxy for unpkg.com to avoid CORS issues on IC network
        const corsProxy = 'https://api.allorigins.win/raw?url=';
        const encodeUrl = (url: string) => corsProxy + encodeURIComponent(url);

        // Version-specific paths
        if (version) {
            paths.push(
                encodeUrl(`https://unpkg.com/${typesPackage}@${version}/index.d.ts`),
                encodeUrl(`https://unpkg.com/${packageName}@${version}/index.d.ts`)
            );
        }

        // Fallback paths without version
        paths.push(
            encodeUrl(`https://unpkg.com/${typesPackage}/index.d.ts`),
            encodeUrl(`https://unpkg.com/${packageName}/index.d.ts`)
        );

        return paths;
    }

    private async fetchTypeDefinitions(packageName: string, version?: string): Promise<string> {
        // Check if there's already an in-flight request for this package
        const requestKey = `${packageName}@${version || 'latest'}`;
        const inFlightRequest = this.inFlightRequests.get(requestKey);
        if (inFlightRequest) {
            console.log(`[JSTypeDefinitionService] Reusing in-flight request for ${requestKey}`);
            return inFlightRequest;
        }

        // Create a new fetch request
        const fetchPromise = this.performFetch(packageName, version);
        this.inFlightRequests.set(requestKey, fetchPromise);

        try {
            const result = await fetchPromise;
            return result;
        } finally {
            // Clean up the in-flight request after completion
            this.inFlightRequests.delete(requestKey);
        }
    }

    private async performFetch(packageName: string, version?: string): Promise<string> {
        // First, try to fetch from unpkg.com
        const paths = packageName.startsWith('@dfinity/') && version
            ? this.getDfinityPaths(packageName, version)
            : this.getRegularPaths(packageName, version);

        // Try each path in order
        for (const path of paths) {
            try {
                // Only log first attempt to reduce noise
                if (paths.indexOf(path) === 0) {
                    console.log(`[JSTypeDefinitionService] Fetching type definitions for ${packageName}...`);
                }
                
                const response = await fetch(path, {
                    method: 'GET',
                    headers: {
                        'Accept': 'text/plain, application/javascript, */*'
                    }
                });

                if (response.ok) {
                    const content = await response.text();
                    if (content && content.trim().length > 0 && !content.includes('404') && !content.includes('Not Found')) {
                        console.log(`[JSTypeDefinitionService] ✓ Fetched types for ${packageName}`);
                        return content;
                    }
                }
            } catch (error) {
                // Silently continue to next path - CORS errors are expected
                const errorMsg = error instanceof Error ? error.message : String(error);
                // Only log if it's not a CORS error (which is expected)
                if (!errorMsg.includes('CORS') && !errorMsg.includes('Failed to fetch')) {
                    console.warn(`[JSTypeDefinitionService] Error fetching ${packageName}:`, errorMsg);
                }
                continue;
            }
        }

        // If all fetches failed, fall back to generated definition (silently for skipped packages)
        if (!this.skipPackages.has(packageName)) {
            console.log(`[JSTypeDefinitionService] Using generated definition for ${packageName}`);
        }
        return this.generateBasicDefinition(packageName);
    }




    private generateBasicDefinition(packageName: string): string {
        // Handle skipped packages with appropriate definitions
        if (this.skipPackages.has(packageName)) {
            switch (packageName) {
                case 'tailwindcss':
                case 'postcss':
                case 'autoprefixer':
                    return `
            declare module "${packageName}" {
                export const config: any;
                export default config;
            }`;
                case 'vite':
                case 'eslint':
                    return `
            declare module "${packageName}" {
                export const defineConfig: (config: any) => any;
                export default defineConfig;
            }`;
                default:
                    return `
            declare module "${packageName}" {
                const content: any;
                export default content;
                export * from content;
            }`;
            }
        }
        
        switch (packageName) {
            case '@dfinity/agent':
                return `
            declare module "@dfinity/agent" {
                import { Principal } from '@dfinity/principal';
                import { IDL } from '@dfinity/candid';

                export declare class Actor {
                    protected constructor();
                    
                    public static createActor<T>(
                        interfaceFactory: InterfaceFactory,
                        configuration: ActorConfig
                    ): T;
                    
                    public static agentOf(actor: unknown): HttpAgent;
                }

                export declare class HttpAgent {
                    public readonly rootKey: null | ArrayBuffer;
                    constructor(options?: HttpAgentOptions);
                    
                    public addTransform(transform: Transform, priority?: number): void;
                    public getPrincipal(): Promise<Principal>;
                    public fetchRootKey(): Promise<ArrayBuffer>;
                    public createActor<T>(
                        interfaceFactory: InterfaceFactory,
                        configuration: ActorConfig
                    ): T;
                }

                export interface ActorMethod<T = any> {
                    (...args: any[]): Promise<T>;
                    withOptions(options: CallOptions): Promise<T>;
                }

                export interface ActorConfig {
                    agent?: HttpAgent;
                    agentOptions?: HttpAgentOptions;
                    canisterId: string | Principal;
                }

                export interface HttpAgentOptions {
                    host?: string;
                    identity?: Identity;
                    fetch?: typeof fetch;
                    callTimeout?: number;
                    fetchRootKey?: boolean;
                }

                export interface Identity {
                    getPrincipal(): Principal;
                    transformRequest(request: HttpAgentRequest): Promise<unknown>;
                }

                export interface InterfaceFactory {
                    idlFactory: IDL.InterfaceFactory;
                    createActor<T>(...args: any[]): T;
                }

                export interface Transform {
                    name: string;
                    priority?: number;
                    transform(request: HttpAgentRequest): Promise<unknown>;
                }

                export interface HttpAgentRequest {
                    request: Request;
                    endpoint: URL;
                }

                export interface CallOptions {
                    cycles?: bigint | number;
                    timeout?: number;
                }
            }`;

            case '@dfinity/principal':
                return `
            declare module "@dfinity/principal" {
                export class Principal {
                    private constructor();
                    
                    static anonymous(): Principal;
                    static fromHex(hex: string): Principal;
                    static fromText(text: string): Principal;
                    static fromUint8Array(arr: Uint8Array): Principal;
                    
                    public isAnonymous(): boolean;
                    public toText(): string;
                    public toHex(): string;
                    public toUint8Array(): Uint8Array;
                    public toString(): string;
                    
                    public equals(other: Principal): boolean;
                    static equal(p1: Principal, p2: Principal): boolean;
                }
            }`;

            case '@dfinity/candid':
                return `
            declare module "@dfinity/candid" {
                export class IDL {
                    private constructor();
                    
                    static Service<T = any>(methods: Record<string, any>): InterfaceFactory;
                    static Function(args: any[], returnType: any): FuncClass;
                    static Record(fields: RecordFieldsType): RecordClass;
                    static Variant(fields: RecordFieldsType): VariantClass;
                    static Optional(type: Type): OptionalClass;
                    static Vector(type: Type): VecClass;
                    
                    static Text: Type;
                    static Int: Type;
                    static Nat: Type;
                    static Float32: Type;
                    static Float64: Type;
                    static Bool: Type;
                    static Null: Type;
                    static Reserved: Type;
                    static Empty: Type;
                    static Principal: Type;
                    static Unknown: Type;
                    static Func: FuncClass;
                }

                interface Type {
                    accept<T>(v: Visitor<T>, d: any): T;
                    covariant(x: any): boolean;
                }

                interface FuncClass extends Type {
                    new (args: Type[], returnType: Type, annotations: string[]): FuncClass;
                }

                interface RecordClass extends Type {
                    new (fields: RecordFieldsType): RecordClass;
                }

                interface VariantClass extends Type {
                    new (fields: RecordFieldsType): VariantClass;
                }

                interface OptionalClass extends Type {
                    new (type: Type): OptionalClass;
                }

                interface VecClass extends Type {
                    new (type: Type): VecClass;
                }

                interface RecordFieldsType {
                    [key: string]: Type;
                }

                interface Visitor<T> {
                    visitType(t: Type, d: any): T;
                    visitNull(t: Type, d: any): T;
                    visitRecord(t: RecordClass, fields: RecordFieldsType, d: any): T;
                    visitVariant(t: VariantClass, fields: RecordFieldsType, d: any): T;
                }

                interface InterfaceFactory {
                    _factory: IDL;
                    _fields: RecordFieldsType;
                }
            }`;

            case 'react':
                return `
            declare module "react" {
                export default interface React {
                    createElement: any;
                }
                export function useState<T>(initialState: T): [T, (newState: T) => void];
                export function useEffect(effect: () => void | (() => void), deps?: any[]): void;
                export function useRef<T>(initialValue: T): { current: T };
                export function useCallback<T extends (...args: any[]) => any>(callback: T, deps: any[]): T;
                export function useMemo<T>(factory: () => T, deps: any[]): T;
            }`;

            case '@types/react':
                return `
            declare module "@types/react" {
                export * from "react";
            }`;

            case 'typescript':
                return `
            declare module "typescript" {
                export interface CompilerOptions {
                    allowJs?: boolean;
                    checkJs?: boolean;
                    declaration?: boolean;
                    declarationMap?: boolean;
                    sourceMap?: boolean;
                    target?: "ES3" | "ES5" | "ES6" | "ES2015" | "ES2016" | "ES2017" | "ES2018" | "ES2019" | "ES2020" | "ESNext";
                    module?: "None" | "CommonJS" | "AMD" | "UMD" | "System" | "ES6" | "ES2015" | "ESNext";
                    strict?: boolean;
                    esModuleInterop?: boolean;
                    jsx?: "preserve" | "react" | "react-native";
                    [option: string]: any;
                }

                export interface System {
                    args: string[];
                    newLine: string;
                    useCaseSensitiveFileNames: boolean;
                    write(s: string): void;
                    readFile(path: string, encoding?: string): string | undefined;
                    writeFile(path: string, data: string, writeByteOrderMark?: boolean): void;
                }

                export function createProgram(
                    rootNames: string[],
                    options: CompilerOptions,
                    host?: System
                ): Program;

                export interface Program {
                    emit(): EmitResult;
                }

                export interface EmitResult {
                    emitSkipped: boolean;
                    diagnostics: Diagnostic[];
                }

                export interface Diagnostic {
                    file: SourceFile | undefined;
                    start: number | undefined;
                    length: number | undefined;
                    messageText: string | DiagnosticMessageChain;
                    category: DiagnosticCategory;
                    code: number;
                }

                export interface SourceFile {
                    fileName: string;
                    text: string;
                }

                export interface DiagnosticMessageChain {
                    messageText: string;
                    category: DiagnosticCategory;
                    code: number;
                    next?: DiagnosticMessageChain[];
                }

                export enum DiagnosticCategory {
                    Warning,
                    Error,
                    Suggestion,
                    Message
                }
            }`;

            case 'tailwindcss':
                return `
            declare module "tailwindcss" {
                interface TailwindConfig {
                    content?: string[];
                    theme?: {
                        extend?: Record<string, any>;
                        [key: string]: any;
                    };
                    plugins?: any[];
                    prefix?: string;
                    important?: boolean;
                    separator?: string;
                    [key: string]: any;
                }

                function tailwind(config?: TailwindConfig): any;
                export = tailwind;
            }`;

            case 'prettier':
                return `
            declare module "prettier" {
                export interface Options {
                    printWidth?: number;
                    tabWidth?: number;
                    useTabs?: boolean;
                    semi?: boolean;
                    singleQuote?: boolean;
                    quoteProps?: 'as-needed' | 'consistent' | 'preserve';
                    jsxSingleQuote?: boolean;
                    trailingComma?: 'none' | 'es5' | 'all';
                    bracketSpacing?: boolean;
                    bracketSameLine?: boolean;
                    arrowParens?: 'avoid' | 'always';
                    parser?: string;
                    filepath?: string;
                    [option: string]: any;
                }

                export function format(source: string, options?: Options): string;
                export function check(source: string, options?: Options): boolean;
                export function resolveConfig(filePath: string): Promise<Options | null>;
            }`;

            case 'eslint':
                return `
            declare module "eslint" {
                export interface Linter {
                    verify(code: string, config: any): LintResult[];
                    verifyAndFix(code: string, config: any): { fixed: boolean; output: string; messages: LintResult[] };
                }

                export interface LintResult {
                    ruleId: string | null;
                    severity: number;
                    message: string;
                    line: number;
                    column: number;
                    nodeType: string;
                    source: string | null;
                    fix?: {
                        range: [number, number];
                        text: string;
                    };
                }

                export class ESLint {
                    constructor(options?: any);
                    lintText(source: string, options?: any): Promise<LintResult[]>;
                    lintFiles(patterns: string[]): Promise<LintResult[]>;
                }
            }`;

            default:
                return `
            declare module "${packageName}" {
                const content: any;
                export default content;
                export * from content;
            }`;
        }
    }

    public async getTypeDefinition(packageName: string): Promise<string> {
        // Skip packages that don't need type definitions
        if (this.skipPackages.has(packageName)) {
            return this.generateBasicDefinition(packageName);
        }

        const cached = this.cache[packageName];
        if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
            return cached.content;
        }

        const packageInfo = this.project.npmPackages?.[0]?.find(pkg => pkg.name === packageName);
        const version = packageInfo?.version;

        const content = await this.fetchTypeDefinitions(packageName, version);

        this.cache[packageName] = {
            content,
            timestamp: Date.now()
        };

        return content;
    }
}